import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Alert,
  Container,
  Paper,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  Stack,
  Card,
  CardContent,
  Chip
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../store";
import { StakeTools, ClaimTools, Reload } from "../store/user/actions";
import NFTCard from "../components/NFTCard";
import UpgradeModal from "./Inventory/UpgradeModal";
import "./Inventory/Inventory.css";
import "./Tools.css";
import { toast } from "react-toastify";
import RefreshIcon from '@mui/icons-material/Refresh';
import GavelIcon from '@mui/icons-material/Gavel';
import ConstructionIcon from '@mui/icons-material/Construction';
import DiamondIcon from '@mui/icons-material/Diamond';
import InventoryIcon from '@mui/icons-material/Inventory';

const uri_prefix = "https://ipfs.io/ipfs/";

export const Tools = () => {
  const userModule = useSelector((state: AppState) => state.userModule);
  const loginInfo = userModule.loginInfo;
  const isLoading = userModule.gameStatus.loading;
  const dispatch = useDispatch<any>();

  const [toolNfts, setToolNfts] = useState([]);
  const [filteredNfts, setFilteredNfts] = useState([]);
  const [statsData, setStatsData] = useState({
    totalTools: 0,
    stakedTools: 0,
    availableTools: 0,
    potentialRewards: 0,
  });

  const filters = ["All", "Shovel", "Pickaxe"];
  const [filterType, setFilterType] = useState("All");
  const [sortBy, setSortBy] = useState("rarity");
  const [tabValue, setTabValue] = useState(0);
  const [rewardSummary, setRewardSummary] = useState({
    xp: 0,
    grass: 0
  });

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedNftForUpgrade, setSelectedNftForUpgrade] = useState<any>(null);
  const [selectedNFTForModal, setSelectedNFTForModal] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);

  const openUpgradeModal = (nft: any) => {
    setSelectedNFTForModal(nft);
    setIsModalOpen(true);
  };

  const closeUpgradeModal = () => {
    setIsModalOpen(false);
    setSelectedNFTForModal(null);
  };

  const handleFilterChange = (e: any) => {
    setFilterType(e.target.value);
  };

  const handleSortByChange = (e: any) => {
    setSortBy(e.target.value);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const processTools = async () => {
      let tools = loginInfo.assets?.filter((item: any) => item?.tool_config);

      tools?.forEach((item: any) => {
        // Map exploration_time to last_claimed from blockchain
        item.last_claimed = item.exploration_time || 0;
      });
      setToolNfts(tools || []);
    };

    processTools();
  }, [loginInfo.assets]);

  useEffect(() => {
    // Calculate reward summary for claimable resources
    const currentUnixTime = Math.floor(Date.now() / 1000);
    const claimableNfts = toolNfts.filter(
      (item: any) => item.last_claimed && item.last_claimed <= currentUnixTime
    );

    let totalXp = 0;
    let totalGrass = 0;

    claimableNfts.forEach((item: any) => {
      if (item.tool_config?.reward_token) {
        const amount = parseFloat(item.tool_config.reward_token.split(" ")[0] || "0");
        const token = item.tool_config.reward_token.split(" ")[1];

        if (token === 'XP') {
          totalXp += amount;
        } else if (token === 'GRASS') {
          totalGrass += amount;
        }
      }
    });

    setRewardSummary({
      xp: parseFloat(totalXp.toFixed(4)),
      grass: parseFloat(totalGrass.toFixed(4))
    });
  }, [toolNfts]);

  useEffect(() => {
    const applyFiltersAndSort = async () => {
      let _filteredNfts = [...toolNfts];
      const currentUnixTime = Math.floor(Date.now() / 1000);

      if (filterType !== "All") {
        _filteredNfts = _filteredNfts.filter((item: any) =>
          item.data.name.includes(filterType)
        );
      }

      // Filter based on tab selection
      if (tabValue === 1) { // Staked
        _filteredNfts = _filteredNfts.filter((item: any) => item.exploration_time);
      } else if (tabValue === 2) { // Unstaked
        _filteredNfts = _filteredNfts.filter((item: any) => !item.exploration_time);
      } else if (tabValue === 3) { // Claimable
        _filteredNfts = _filteredNfts.filter(
          (item: any) => item.last_claimed && item.last_claimed <= currentUnixTime
        );
      }

      if (sortBy === "rarity") {
        _filteredNfts.sort((a: any, b: any) => {
          const rewardA = parseFloat(
            b.tool_config.reward_token.split(" ")?.[0] || "0"
          );
          const rewardB = parseFloat(
            a.tool_config.reward_token.split(" ")?.[0] || "0"
          );
          return rewardA - rewardB;
        });
      } else if (sortBy === "time") {
        _filteredNfts.sort(
          (a: any, b: any) => b.exploration_time - a.exploration_time
        );
      }
      setFilteredNfts(_filteredNfts);

      // Update statistics
      const stakedTools = toolNfts.filter((item: any) => item.exploration_time).length;
      const availableTools = toolNfts.filter((item: any) => !item.exploration_time).length;
      const potentialRewards = toolNfts.reduce((total: number, item: any) => {
        if (item.exploration_time && item.exploration_time < currentUnixTime) {
          return total + parseFloat(item.tool_config.reward_token.split(" ")[0] || "0");
        }
        return total;
      }, 0);

      setStatsData({
        totalTools: toolNfts.length,
        stakedTools,
        availableTools,
        potentialRewards: parseFloat(potentialRewards.toFixed(4))
      });
    };

    applyFiltersAndSort();
  }, [toolNfts, filterType, sortBy, tabValue]);

  const handleStake = async (assetId: string) => {
    const selectedTools = toolNfts.filter(
      (item: any) => assetId === item.asset_id
    );
    let rewardAmount = 0;
    selectedTools.forEach((item: any) => {
      const rewardToken = item.tool_config.reward_token;
      const idx = parseFloat(rewardToken.split(" ")?.[0] || "-1");
      if (idx !== -1) {
        rewardAmount += idx;
      }
    });

    const success = await dispatch(
      StakeTools([assetId], rewardAmount, filterType)
    );

    if (success) {
      toast.success("Tool staked successfully.");
    }
  };

  const handleClaim = async (assetId: any) => {
    const success = await dispatch(ClaimTools([assetId]));
    if (success) {
      toast.success("Tool claimed successfully.");
      dispatch(Reload());
    }
  };

  const handleStakeAll = async () => {
    const stakeableNfts = filteredNfts.filter(
      (item: any) => !item.exploration_time
    );

    if (stakeableNfts.length === 0) {
      toast.error("No stakeable NFTs available.");
      return;
    }

    const assetIds = stakeableNfts.map((item: any) => item.asset_id);

    let totalRewardAmount = 0;
    stakeableNfts.forEach((item: any) => {
      const rewardToken = item.tool_config.reward_token;
      const idx = parseFloat(rewardToken.split(" ")?.[0] || "-1");
      if (idx !== -1) {
        totalRewardAmount += idx;
      }
    });

    const success = await dispatch(
      StakeTools(assetIds, totalRewardAmount, filterType)
    );

    if (success) {
      toast.success(`${assetIds.length} tools staked successfully.`);
    }
  };

  const handleClaimAll = async () => {
    const currentUnixTime = Math.floor(Date.now() / 1000);
    const claimableNfts = filteredNfts.filter(
      (item: any) => item.last_claimed && item.last_claimed <= currentUnixTime
    );

    if (claimableNfts.length === 0) {
      toast.warning("No claimable NFTs available.");
      return;
    }

    // Instead of claiming all at once, batch them in groups of max 10
    const BATCH_SIZE = 100;
    const totalBatches = Math.ceil(claimableNfts.length / BATCH_SIZE);
    let successCount = 0;
    let failureCount = 0;

    // Show initial processing toast
    const processingToast = toast.info(
      `Processing ${claimableNfts.length} tools in ${totalBatches} batches...`,
      { autoClose: false }
    );

    try {
      for (let i = 0; i < claimableNfts.length; i += BATCH_SIZE) {
        const batchNfts = claimableNfts.slice(i, i + BATCH_SIZE);
        const batchIds = batchNfts.map((item: any) => item.asset_id);

        // Update progress in toast
        toast.update(processingToast, {
          render: `Processing batch ${Math.floor(i / BATCH_SIZE) + 1}/${totalBatches}...`,
          autoClose: false
        });

        // Process batch
        const success = await dispatch(ClaimTools(batchIds));

        if (success) {
          successCount += batchIds.length;
        } else {
          failureCount += batchIds.length;
        }

        // Wait a moment between batches to avoid overwhelming the backend
        if (i + BATCH_SIZE < claimableNfts.length) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Close the processing toast
      toast.dismiss(processingToast);

      // Show final results
      if (successCount > 0) {
        toast.success(`Successfully claimed ${successCount} tools`);
      }

      if (failureCount > 0) {
        toast.error(`Failed to claim ${failureCount} tools`);
      }

      // Reload data without refreshing page
      dispatch(Reload());
    } catch (error) {
      console.error("Claim all error:", error);
      toast.dismiss(processingToast);
      toast.error("An error occurred while claiming tools");
    }
  };

  const handleRefresh = () => {
    dispatch(Reload());
    toast.info("Refreshing data...");
  };

  // Upgrade Modal Handlers
  const handleOpenUpgradeModal = (nft: any) => {
    setSelectedNftForUpgrade(nft);
    setIsUpgradeModalOpen(true);
  };

  const handleCloseUpgradeModal = () => {
    setIsUpgradeModalOpen(false);
    setSelectedNftForUpgrade(null);
  };

  const handleUpgrade = () => {
    if (selectedNftForUpgrade) {
      // dispatch(UpgradeTool(selectedNftForUpgrade.asset_id)); // Replace with your actual dispatch
      handleCloseUpgradeModal();
    }
  };

  const handleUpgradeConfirm = async () => {
    if (!selectedNFTForModal) return;

    setIsUpgrading(true);
    try {
      // Here you'd implement the actual upgrade logic
      // For example:
      // await dispatch(UpgradeNFT(selectedNFTForModal.asset_id));

      // Mock delayed response
      await new Promise(resolve => setTimeout(resolve, 1500));

      toast.success("Tool upgraded successfully!");
      closeUpgradeModal();
    } catch (error) {
      console.error("Upgrade failed:", error);
      toast.error("Failed to upgrade tool. Please try again.");
    } finally {
      setIsUpgrading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 4,
          background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
          backgroundSize: '100% 100%',
          borderRadius: '16px',
          position: 'relative',
          overflow: 'hidden',
          border: '4px solid #7d5014',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
        }}
      >
        <Box sx={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'center', sm: 'flex-start' },
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 0 } }}>
            <ConstructionIcon sx={{ color: "#f5e1a3", mr: 2, fontSize: "2.5rem" }} />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  color: "#f5e1a3",
                  fontWeight: "bold",
                  textShadow: '2px 2px 4px rgba(0,0,0,0.6)'
                }}
              >
                Tools Inventory
              </Typography>
              <Typography variant="body1" color="rgba(255,255,255,0.9)" sx={{ mt: 0.5, textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
                Manage your tools, stake them to earn rewards, and upgrade your collection
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{
              color: "#f5e1a3",
              borderColor: "#f5e1a3",
              "&:hover": {
                borderColor: "#fff",
                backgroundColor: "rgba(245, 225, 163, 0.1)",
              },
              alignSelf: { xs: 'center', sm: 'flex-start' }
            }}
          >
            Refresh Data
          </Button>
        </Box>

        {/* Wood grain overlay */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
          opacity: 0.4,
          zIndex: 1
        }} />

        {/* Decorative elements */}
        <Box sx={{
          position: 'absolute',
          right: { xs: -30, md: 30 },
          top: 0,
          opacity: 0.15,
          zIndex: 1,
          transform: 'rotate(-10deg)'
        }}>
          <ConstructionIcon sx={{ fontSize: 120 }} />
        </Box>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', flexDirection: 'column', gap: 2 }}>
          <CircularProgress size={60} sx={{ color: '#d9534f' }} />
          <Typography variant="h6" color="white" sx={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>Loading your tools...</Typography>
        </Box>
      ) : toolNfts && toolNfts.length > 0 ? (
        <>
          {/* Stats Cards Row */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
                border: '4px solid #5c3c10',
                borderRadius: '12px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Wood grain pattern overlay */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
                  opacity: 0.3,
                  zIndex: 0
                }} />
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      bgcolor: 'rgba(77, 124, 254, 0.2)',
                      borderRadius: '12px',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <InventoryIcon sx={{ fontSize: 36, color: '#4d7cfe' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="rgba(255,255,255,0.7)">
                        Total Tools
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="white">
                        {statsData.totalTools}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
                border: '4px solid #5c3c10',
                borderRadius: '12px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Wood grain pattern overlay */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
                  opacity: 0.3,
                  zIndex: 0
                }} />
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      bgcolor: 'rgba(46, 202, 106, 0.2)',
                      borderRadius: '12px',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <GavelIcon sx={{ fontSize: 36, color: '#2eca6a' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="rgba(255,255,255,0.7)">
                        Staked
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="white">
                        {statsData.stakedTools}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
                border: '4px solid #5c3c10',
                borderRadius: '12px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Wood grain pattern overlay */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
                  opacity: 0.3,
                  zIndex: 0
                }} />
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      bgcolor: 'rgba(255, 173, 70, 0.2)',
                      borderRadius: '12px',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <ConstructionIcon sx={{ fontSize: 36, color: '#ffad46' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="rgba(255,255,255,0.7)">
                        Available
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="white">
                        {statsData.availableTools}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                height: '100%',
                background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
                border: '4px solid #5c3c10',
                borderRadius: '12px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Wood grain pattern overlay */}
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
                  opacity: 0.3,
                  zIndex: 0
                }} />
                <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{
                      bgcolor: 'rgba(233, 76, 88, 0.2)',
                      borderRadius: '12px',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <DiamondIcon sx={{ fontSize: 36, color: '#e94c58' }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="rgba(255,255,255,0.7)">
                        Claimable
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color="white">
                        {statsData.potentialRewards}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Main Content Paper */}
          <Paper
            elevation={3}
            className="wood-panel"
            sx={{
              p: 3,
              borderRadius: '16px',
              position: 'relative',
              background: 'linear-gradient(135deg, #3d2c0a 0%, #5c3c10 100%)',
              border: '8px solid #7d5014',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
              color: '#f5e1a3', // Improved font color for contrast
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.35) 0px, rgba(92, 60, 16, 0.35) 2px, transparent 2px, transparent 4px)',
                opacity: 0.5,
                zIndex: 0
              },
              '& > *': {
                position: 'relative',
                zIndex: 1
              }
            }}
          >
            {/* Tabs & Filters Container */}
            <Box sx={{ mb: 4 }}>
              {/* Tabs for filtering */}
              <Box sx={{ width: '100%', mb: 3, borderBottom: '1px solid rgba(245,225,163,0.2)' }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  sx={{
                    '& .MuiTabs-indicator': { backgroundColor: '#e9b44c' },
                    '& .MuiTab-root': {
                      color: 'rgba(245,225,163,0.7)',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      textTransform: 'none',
                      minWidth: '120px'
                    },
                    '& .Mui-selected': { color: '#f5e1a3 !important' }
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="All Tools" />
                  <Tab label="Staked Tools" />
                  <Tab label="Available Tools" />
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>Claimable</span>
                        <Chip
                          size="small"
                          label={`XP: ${rewardSummary.xp} | GRASS: ${rewardSummary.grass}`}
                          sx={{
                            bgcolor: 'rgba(233, 180, 76, 0.2)',
                            color: '#f5e1a3',
                            border: '1px solid rgba(233, 180, 76, 0.4)',
                            fontSize: '0.7rem',
                            height: '20px'
                          }}
                        />
                      </Box>
                    }
                  />
                  <Tab label="Instructions" />
                </Tabs>
              </Box>

              {/* Filter, Sort and Action Buttons */}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="filter-label" sx={{ color: '#f5e1a3' }}>Type</InputLabel>
                      <Select
                        labelId="filter-label"
                        value={filterType}
                        onChange={handleFilterChange}
                        label="Type"
                        sx={{
                          color: '#f5e1a3',
                          '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(245,225,163,0.3)' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(245,225,163,0.5)' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e9b44c' },
                          '.MuiSvgIcon-root': { color: '#f5e1a3' }
                        }}
                      >
                        {filters.map((filter) => (
                          <MenuItem key={filter} value={filter}>{filter}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="sortby-label" sx={{ color: '#f5e1a3' }}>Sort By</InputLabel>
                      <Select
                        labelId="sortby-label"
                        value={sortBy}
                        onChange={handleSortByChange}
                        label="Sort By"
                        sx={{
                          color: '#f5e1a3',
                          '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(245,225,163,0.3)' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(245,225,163,0.5)' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e9b44c' },
                          '.MuiSvgIcon-root': { color: '#f5e1a3' }
                        }}
                      >
                        <MenuItem value="rarity">Rarity</MenuItem>
                        <MenuItem value="time">Time</MenuItem>
                      </Select>
                    </FormControl>

                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={handleRefresh}
                      size="medium"
                      sx={{
                        color: '#f5e1a3',
                        borderColor: 'rgba(245,225,163,0.3)',
                        '&:hover': { borderColor: '#f5e1a3', backgroundColor: 'rgba(245,225,163,0.08)' }
                      }}
                    >
                      Refresh
                    </Button>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={handleStakeAll}
                      disabled={filteredNfts.filter((item: any) => !item.exploration_time).length === 0}
                      sx={{
                        backgroundColor: '#2eca6a',
                        color: '#fffbe6',
                        fontWeight: 'bold',
                        '&:hover': { backgroundColor: '#25a65a' },
                        '&.Mui-disabled': { backgroundColor: 'rgba(46, 202, 106, 0.3)', color: 'rgba(255,255,255,0.5)' },
                        borderRadius: '8px',
                        px: 3,
                        boxShadow: '0 4px 16px 0 rgba(46,202,106,0.15), 0 1.5px 4px 0 rgba(0,0,0,0.10)'
                      }}
                    >
                      Stake All ({filteredNfts.filter((item: any) => !item.exploration_time).length})
                    </Button>

                    <Button
                      variant="contained"
                      disableElevation
                      onClick={handleClaimAll}
                      disabled={filteredNfts.filter((item: any) => item.exploration_time && item.exploration_time < Date.now() / 1000).length === 0}
                      sx={{
                        background: 'linear-gradient(90deg, #e94c58 60%, #e9b44c 100%)',
                        color: '#fffbe6',
                        fontWeight: 'bold',
                        '&:hover': { background: 'linear-gradient(90deg, #d43545 60%, #e9b44c 100%)' },
                        '&.Mui-disabled': { backgroundColor: 'rgba(233, 76, 88, 0.3)', color: 'rgba(255,255,255,0.5)' },
                        borderRadius: '8px',
                        px: 3
                      }}
                    >
                      Claim All ({filteredNfts.filter((item: any) => item.exploration_time && item.exploration_time < Date.now() / 1000).length})
                    </Button>
                  </Stack>
                </Grid>
              </Grid>

              {/* Results count and info */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ color: '#e9b44c' }}>
                  Showing {filteredNfts.length} of {toolNfts.length} tools
                </Typography>
              </Box>
            </Box>

            {/* NFT Cards Container */}

            {tabValue === 4 ? (
              <Box className="game-paper" sx={{ mt: 2, p: 3, borderRadius: '12px', background: 'rgba(245,225,163,0.07)' }}>
                <Typography variant="h5" sx={{ color: '#f5e1a3', fontWeight: 'bold', mb: 2 }}>
                  Tool Staking Instructions
                </Typography>

                <Typography variant="body1" sx={{ mb: 2, color: '#f5e1a3' }}>
                  Tools are essential for gathering resources in the game. Follow these steps to make the most of your tools:
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: '#e9b44c', fontWeight: 'bold', mb: 1 }}>
                    How to Stake Tools
                  </Typography>
                  <ol style={{ color: '#f5e1a3', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}>Select an available tool from your inventory</li>
                    <li style={{ marginBottom: '8px' }}>Click the "Stake" button on the tool card</li>
                    <li style={{ marginBottom: '8px' }}>Confirm the transaction when prompted</li>
                    <li style={{ marginBottom: '8px' }}>Once staked, your tool will start generating rewards automatically</li>
                  </ol>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: '#e9b44c', fontWeight: 'bold', mb: 1 }}>
                    Rewards and Claiming
                  </Typography>
                  <ul style={{ color: '#f5e1a3', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}>Each tool generates specific resources (XP or GRASS) at a fixed rate per day</li>
                    <li style={{ marginBottom: '8px' }}>When a tool is ready for claiming, it will be marked as "Ready"</li>
                    <li style={{ marginBottom: '8px' }}>Click the "Claim" button to collect your rewards</li>
                    <li style={{ marginBottom: '8px' }}>You can also use the "Claim All" button to collect rewards from all eligible tools at once</li>
                  </ul>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: '#e9b44c', fontWeight: 'bold', mb: 1 }}>
                    Tool Upgrades
                  </Typography>
                  <ul style={{ color: '#f5e1a3', paddingLeft: '20px' }}>
                    <li style={{ marginBottom: '8px' }}>Tools can be upgraded to increase their efficiency and reward rates</li>
                    <li style={{ marginBottom: '8px' }}>Click the "Upgrade" button on a tool card to view upgrade options</li>
                    <li style={{ marginBottom: '8px' }}>Higher level tools generate more rewards and may have additional benefits</li>
                    <li style={{ marginBottom: '8px' }}>Upgrading requires resources and has a cooldown period</li>
                  </ul>
                </Box>

                <Box sx={{ bgcolor: 'rgba(233, 180, 76, 0.15)', p: 2, borderRadius: '8px', border: '1px solid rgba(233, 180, 76, 0.3)' }}>
                  <Typography variant="subtitle1" sx={{ color: '#e9b44c', fontWeight: 'bold' }}>
                    Pro Tip:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#f5e1a3' }}>
                    For maximum efficiency, always keep your tools staked and claim rewards regularly. Consider upgrading your most productive tools first to maximize your resource generation.
                  </Typography>
                </Box>
              </Box>
            ) : filteredNfts.length > 0 ? (
                <Box
                  sx={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    mt: 2,
                    p: 1
                  }}
                >
                  <Grid
                    container
                    spacing={3}
                    columns={{ xs: 2, sm: 3, md: 4, lg: 5, xl: 6 }}
                  >
                    {filteredNfts?.map((item: any, index: number) => (
                      <NFTCard
                        key={item.asset_id}
                        uri={uri_prefix + item.data.img}
                        name={item.data.name}
                        assetID={item.asset_id}
                        stakedTime={item.exploration_time}
                        onClaim={() => handleClaim(item)}
                        onStake={() => handleStake(item.asset_id)}
                        nftData={item}
                        onUpgrade={() => openUpgradeModal(item)}
                      />
                    ))}
                  </Grid>
                </Box>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Alert
                    severity="info"
                    sx={{
                      bgcolor: 'rgba(41, 121, 255, 0.1)',
                      color: '#f5e1a3',
                      border: '1px solid rgba(41, 121, 255, 0.3)',
                      '& .MuiAlert-icon': { color: '#e9b44c' }
                    }}
                  >
                    No tools match the current filters. Try changing the filter options.
                  </Alert>
                </Box>
              )
            }
          </Paper>
        </>
      ) : (
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)',
            border: '8px solid #5c3c10',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Wood grain pattern overlay */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)',
            opacity: 0.3,
            zIndex: 0
          }} />
          <Box sx={{ maxWidth: '500px', mx: 'auto', py: 5, position: 'relative', zIndex: 1 }}>
            <ConstructionIcon sx={{ fontSize: 80, color: 'rgba(255,255,255,0.2)', mb: 3 }} />
            <Typography variant="h5" color="white" gutterBottom>
              You don't have any tools yet
            </Typography>
            <Typography variant="body1" color="rgba(255,255,255,0.7)" sx={{ mb: 4 }}>
              Tools help you collect resources in the game. Get started by purchasing your first tool.
            </Typography>
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#d9534f',
                '&:hover': { backgroundColor: '#c9302c' },
                borderRadius: '8px',
                px: 4,
                py: 1.5
              }}
              href="https://wax.atomichub.io/market?blockchain=wax-mainnet&collection_name=waxdeerjc&order=asc&primary_chain=wax-mainnet&schema_name=tools&sort=price&symbol=WAX#sales"
              target="_blank"
              rel="noopener noreferrer"
            >
              Browse Tools Marketplace
            </Button>
          </Box>
        </Paper>
      )}

      <UpgradeModal
        open={isModalOpen}
        onClose={closeUpgradeModal}
        nft={selectedNFTForModal}
        onConfirm={handleUpgradeConfirm}
        isUpgrading={isUpgrading}
        type="tool"
      />
    </Container>
  );
};
